import { SphereButton } from "@components/common/sphere-button";
import { FaroButtonContained } from "@components/common/faro-button-contained";
import { AutoCompleteMessage } from "@components/common/faro-text-field/faro-text-field-message";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { AutoCompleteMemberOption } from "@components/common/members-autocomplete/members-autocomplete-types";
import { createMemberOption } from "@components/common/members-autocomplete/members-autocomplete-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { UserId } from "@custom-types/base-types";
import { BaseTeamProps } from "@custom-types/teams-types";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { Stack } from "@mui/material";
import {
  isNewEmailValid,
  shouldDisableSendInvite,
  validateInputChange,
  validateMembers,
} from "@pages/members/teams/teams-utils";
import { useAppParams } from "@router/router-helper";
import { INVITE_DIALOG_CLOSE_DELAY_MS } from "@src/constants/invite-constants";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { setOne } from "@store/members/members-slice";
import { convertToMemberTypes } from "@store/members/members-slice-utils";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedTeamMembersSelector } from "@store/teams/teams-selector";
import { addMembersToTeam } from "@store/teams/teams-slice-thunk";
import { nounPluralize } from "@utils/data-display";
import { delay } from "@utils/time-utils";
import { TeamEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";
import { Dialog } from "@flat-ui";

interface Props extends BaseTeamProps {
  /**
   * Determines the variant of the invite member button
   *  - "outlined" (default): Contained button with white background and blue border
   *  - "filled": Normal button with blue background
   */
  buttonVariant?: "outlined" | "filled";
}

/** Renders the invite member button and the functionality in team page */
export function InviteMemberToTeam({
  team,
  buttonVariant = "outlined",
}: Props): JSX.Element | null {
  const companyMembers = useCompanyMembers();
  const dispatch = useAppDispatch();
  const { companyId } = useAppParams();
  const { respondToTeamInvite } = useRespondToInvite();
  const { handleErrorWithToast } = useErrorContext();
  const { trackEvent } = useTrackEvent();
  const teamMembers = useAppSelector(selectedTeamMembersSelector);
  const { canInviteMembersToTeam } = useHasUserValidRoleCompanyLevel();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [message, setMessage] = useState<AutoCompleteMessage | undefined>();

  /** Flag whether the input field in the autocomplete component has a valid email */
  const [isMemberInputValid, setIsMemberInputValid] = useState<boolean>(false);

  // Disable the `Send Invite` button if no user has been selected and the input is not a valid email.
  const shouldSendDisable = shouldDisableSendInvite(
    selectedMembers,
    isMemberInputValid
  );

  /** Reformation company members data for faro autocomplete component */
  const memberOptions: AutoCompleteMemberOption[] = useMemo(
    () =>
      companyMembers.map((member) => {
        const isAlreadyMemberInTeam = teamMembers.some(
          (teamMember) => member.identity === teamMember.userResponse.identity
        );

        return createMemberOption({
          member,
          isDisabled: isAlreadyMemberInTeam,
          disabledMessage: isAlreadyMemberInTeam
            ? `Already in this ${TEAM_DISPLAY_NAME}`
            : "",
        });
      }),
    [companyMembers, teamMembers]
  );

  if (!canInviteMembersToTeam) {
    return null;
  }

  /** Default setup when invite dialog open */
  function onOpenInviteDialog(): void {
    setMessage(undefined);
    setSelectedMembers([]);
    setIsDialogOpen(true);
  }

  function handleMemberSelect(members: UserId[]): void {
    const { message, isValid } = validateMembers(members, TEAM_DISPLAY_NAME);

    setMessage(message);
    setIsMemberInputValid(isValid);
    setSelectedMembers(members);
  }

  /** Close the dialog and reset loading state */
  async function closeDialog(): Promise<void> {
    setIsDialogOpen(false);
    // Delay to ensure dialog closes before resetting loading state
    // This prevents duplicate invitation requests
    await delay(INVITE_DIALOG_CLOSE_DELAY_MS);
    setIsLoading(false);
  }

  /** Submit members to be invited */
  async function handleConfirm(): Promise<void> {
    setIsLoading(true);

    trackEvent({
      name: TeamEvents.inviteMember,
      props: { numberOfMembers: selectedMembers.length },
    });

    try {
      if (!companyId) {
        throw new Error("companyId is required");
      }

      // Add members
      const response = await dispatch(
        addMembersToTeam({
          companyId,
          teamId: team.id,
          members: selectedMembers,
        })
      ).unwrap();

      const newMemberResponse = response.data.filter(
        (allMembers) =>
          !companyMembers.some(
            (oldMembers) =>
              oldMembers.identity === allMembers.userResponse.identity
          )
      );

      if (newMemberResponse && newMemberResponse.length > 0) {
        newMemberResponse.forEach((member) => {
          const newMember = convertToMemberTypes(member.userResponse);

          /**
           * The new member should be added to the member store as well.
           * We should use the setOne because we are updating the company role for all members
           * that are present in this list for a Project Role and we should have a Company Role.
           * If we update the role to project in the members store, we will not be able to remove
           * these members from the member page. The user needs to reload the page to have the company
           * role again.
           */
          dispatch(setOne(newMember));
        });
      }

      respondToTeamInvite(response, selectedMembers.length);

      void closeDialog();
    } catch (error) {
      setIsLoading(false);

      handleErrorWithToast({
        id: `inviteMemberToTeam-${Date.now().toString()}`,
        title: `Cannot invite the ${nounPluralize({
          shouldShowCounter: false,
          counter: selectedMembers.length,
          word: "member",
        })}`,
        error,
      });
    }
  }

  /**
   * Function to be called when the input value changes
   * It sets the valid emails to the addedEmails state and removes them from the input value
   */
  function onInputChange(value: string): void {
    const { message, isValid } = validateInputChange(
      memberOptions,
      companyMembers,
      value,
      TEAM_DISPLAY_NAME
    );

    setMessage(message);
    setIsMemberInputValid(isValid);
  }

  return (
    <>
      {buttonVariant === "filled" ? (
        <SphereButton onClick={onOpenInviteDialog}>Invite members</SphereButton>
      ) : (
        <FaroButtonContained onClick={onOpenInviteDialog}>
          Invite members
        </FaroButtonContained>
      )}

      <Dialog
        title={`Invite members to ${TEAM_DISPLAY_NAME}`}
        confirmText="Send Invite"
        open={isDialogOpen}
        onConfirm={handleConfirm}
        showSpinner={isLoading}
        isConfirmDisabled={shouldSendDisable}
        onCancel={() => setIsDialogOpen(false)}
      >
        <Stack>
          <MembersAutocomplete
            options={memberOptions}
            handleChange={handleMemberSelect}
            onInputChange={onInputChange}
            validateNewOption={(email) =>
              isNewEmailValid(email, companyMembers)
            }
            message={message}
            labelTitle="Members"
            hasAutoFocus={true}
          />
        </Stack>
      </Dialog>
    </>
  );
}
